import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2, Plus, Loader2, TrendingUp, TrendingDown, Shield, RotateCcw } from "lucide-react";
import { YearEndChecklist } from "@/components/YearEndChecklist";

const ADJUSTMENT_TYPES = [
  {
    id: "accrual_active",
    label: "Aktive Rechnungsabgrenzung (ARA)",
    description: "Bezahlter Aufwand, der (teilweise) ins nächste Jahr gehört. Z.B. Versicherung für 2026 im Dezember 2025 bezahlt.",
    icon: TrendingUp,
    tone: "bg-sky-50 border-sky-200",
    iconBg: "bg-sky-100 text-sky-700",
    balanceLabel: "ARA-Konto",
    balanceDefault: "1300",
    accountLabel: "Aufwandskonto",
    accountPlaceholder: "z.B. 6000 (Raum)",
  },
  {
    id: "accrual_passive",
    label: "Passive Rechnungsabgrenzung (PRA)",
    description: "Erhaltener Ertrag, der (teilweise) ins nächste Jahr gehört. Z.B. Abo-Zahlung Dez 25 für Jan-Dez 26.",
    icon: TrendingDown,
    tone: "bg-violet-50 border-violet-200",
    iconBg: "bg-violet-100 text-violet-700",
    balanceLabel: "PRA-Konto",
    balanceDefault: "2300",
    accountLabel: "Ertragskonto",
    accountPlaceholder: "z.B. 3000 (Dienstleistung)",
  },
  {
    id: "provision",
    label: "Rückstellung",
    description: "Drohender Aufwand, der noch nicht gebucht ist. Z.B. Steuerrückstellung für das laufende Jahr.",
    icon: Shield,
    tone: "bg-amber-50 border-amber-200",
    iconBg: "bg-amber-100 text-amber-700",
    balanceLabel: "Rückstellungs-Konto",
    balanceDefault: "2330",
    accountLabel: "Aufwandskonto",
    accountPlaceholder: "z.B. 7900 (Steueraufwand)",
  },
  {
    id: "reversal_ara",
    label: "Auflösung ARA (Vorjahr)",
    description: "Im Vorjahr aktivierte Abgrenzung im neuen Jahr auflösen. Buchung: Aufwand / 1300.",
    icon: RotateCcw,
    tone: "bg-emerald-50 border-emerald-200",
    iconBg: "bg-emerald-100 text-emerald-700",
    balanceLabel: "ARA-Konto",
    balanceDefault: "1300",
    accountLabel: "Aufwandskonto",
    accountPlaceholder: "z.B. 6000 (Raum)",
  },
  {
    id: "reversal_pra",
    label: "Auflösung PRA (Vorjahr)",
    description: "Im Vorjahr passivierte Abgrenzung im neuen Jahr als Ertrag auflösen. Buchung: 2300 / Ertrag.",
    icon: RotateCcw,
    tone: "bg-teal-50 border-teal-200",
    iconBg: "bg-teal-100 text-teal-700",
    balanceLabel: "PRA-Konto",
    balanceDefault: "2300",
    accountLabel: "Ertragskonto",
    accountPlaceholder: "z.B. 3000 (Dienstleistung)",
  },
];

function formatCHF(value) {
  return Number(value || 0).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function YearEnd() {
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [preview, setPreview] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resultAccount, setResultAccount] = useState("2970");
  const [closingStatus, setClosingStatus] = useState(null);

  // Form state for new adjustment
  const [form, setForm] = useState({
    adjustment_type: "accrual_active",
    amount: "",
    expense_or_income_account_id: "",
    balance_account_id: "",
    description: "",
  });
  const [submittingAdj, setSubmittingAdj] = useState(false);

  const companyId = localStorage.getItem("demoCompanyId");

  // Iter125: Vorlagen & Smart Suggestions
  const [templates, setTemplates] = useState([]);
  const [smartSuggestions, setSmartSuggestions] = useState([]);

  const loadData = useCallback(async () => {
    const y = parseInt(year, 10);
    if (!companyId || !y || y < 1900 || y > 3000) return;
    setLoading(true);
    setError("");
    try {
      const y = parseInt(year, 10);
      const [prev, adj, tpl, sug] = await Promise.all([
        apiClient.get(`/companies/${companyId}/year-end-preview`, { params: { year: y } }),
        apiClient.get(`/companies/${companyId}/year-end-adjustments`, { params: { year: y } }),
        apiClient.get(`/companies/${companyId}/year-end-adjustment-templates`, { params: { year: y } }).catch(() => ({ data: [] })),
        apiClient.get(`/companies/${companyId}/year-end-smart-suggestions`, { params: { year: y } }).catch(() => ({ data: [] })),
      ]);
      setPreview(prev.data);
      setAdjustments(adj.data || []);
      setTemplates(tpl.data || []);
      setSmartSuggestions(sug.data || []);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || "Fehler beim Laden.");
    } finally {
      setLoading(false);
    }
  }, [companyId, year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const loadClosingStatus = useCallback(async () => {
    const y = parseInt(year, 10);
    if (!companyId || !y || y < 1900 || y > 3000) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/year-end-status`, { params: { year: y } });
      setClosingStatus(r.data);
    } catch {
      setClosingStatus(null);
    }
  }, [companyId, year]);

  useEffect(() => {
    loadClosingStatus();
  }, [loadClosingStatus]);

  const requestApproval = async () => {
    try {
      await apiClient.post(`/companies/${companyId}/year-end-approval/request`, null, { params: { year: parseInt(year, 10) } });
      toast.success("Treuhänder-Freigabe angefragt");
      await loadClosingStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Anfrage fehlgeschlagen"); }
  };

  const approveYear = async () => {
    if (!window.confirm(`Jahresabschluss ${year} als Treuhänder prüfen und freigeben?`)) return;
    try {
      await apiClient.post(`/companies/${companyId}/year-end-approval/approve`, null, { params: { year: parseInt(year, 10) } });
      toast.success("Abschluss freigegeben");
      await loadClosingStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Freigabe fehlgeschlagen"); }
  };

  const lockYear = async (lock) => {
    const y = parseInt(year, 10);
    if (!window.confirm(lock
      ? `Geschäftsjahr ${y} definitiv sperren? Danach sind keine Buchungen oder Änderungen bis 31.12.${y} mehr möglich.`
      : `Definitiv-Sperre für ${y} wirklich aufheben?`)) return;
    try {
      if (lock) {
        await apiClient.post(`/companies/${companyId}/year-end-lock`, null, { params: { year: y } });
        toast.success(`Geschäftsjahr ${y} definitiv gesperrt`);
      } else {
        await apiClient.delete(`/companies/${companyId}/year-end-lock`, { params: { year: y } });
        toast.success(`Sperre für ${y} aufgehoben`);
      }
      await loadClosingStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const undoYearEnd = async () => {
    if (!window.confirm(`Abschlussbuchung ${year} wirklich rückgängig machen?`)) return;
    try {
      await apiClient.delete(`/companies/${companyId}/year-end-closing`, { params: { year: parseInt(year, 10) } });
      toast.success("Abschlussbuchung rückgängig gemacht");
      await loadClosingStatus();
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Rückgängig fehlgeschlagen");
    }
  };

  const typeMeta = ADJUSTMENT_TYPES.find((t) => t.id === form.adjustment_type) || ADJUSTMENT_TYPES[0];

  const submitAdjustment = async (e) => {
    e.preventDefault();
    if (!form.amount || !form.expense_or_income_account_id || !form.description) {
      toast.error("Bitte alle Pflichtfelder ausfüllen.");
      return;
    }
    setSubmittingAdj(true);
    try {
      await apiClient.post(`/companies/${companyId}/year-end-adjustments`, {
        year: parseInt(year, 10),
        adjustment_type: form.adjustment_type,
        amount: parseFloat(form.amount),
        expense_or_income_account_id: form.expense_or_income_account_id,
        balance_account_id: form.balance_account_id || null,
        description: form.description,
      });
      toast.success("Anpassungsbuchung erstellt");
      setForm({
        adjustment_type: form.adjustment_type,
        amount: "",
        expense_or_income_account_id: "",
        balance_account_id: "",
        description: "",
      });
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setSubmittingAdj(false);
    }
  };

  const deleteAdjustment = async (entryId) => {
    if (!window.confirm("Anpassungsbuchung wirklich löschen?")) return;
    try {
      await apiClient.delete(`/companies/${companyId}/year-end-adjustments/${entryId}`);
      toast.success("Gelöscht");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const executeYearEnd = async () => {
    if (!resultAccount) {
      toast.error("Bitte Ergebniskonto angeben.");
      return;
    }
    if (!window.confirm(`Jahresabschluss ${year}: Ergebnis auf Konto ${resultAccount} (Gewinnvortrag) übertragen?`)) return;
    setLoading(true);
    try {
      await apiClient.post(`/companies/${companyId}/year-end-execute`, {
        year: parseInt(year, 10),
        result_account_id: resultAccount,
      });
      toast.success(`Jahresabschluss ${year} gebucht`);
      await loadData();
      await loadClosingStatus();
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Buchen");
      toast.error(err.response?.data?.detail || "Fehler beim Buchen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="year-end-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Jahresabschluss</h1>
        <p className="mt-1 text-sm text-slate-500">
          Geführter Prozess: Abgrenzungen & Rückstellungen erfassen → Ergebnis prüfen → Abschlussbuchung ausführen.
        </p>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="year-end-error">
          {error}
        </div>
      )}

      {/* Step 1: Year */}
      <Card className="p-4 max-w-xl border border-slate-200 bg-white" data-testid="year-end-form-card">
        <div className="flex items-end gap-3">
          <div className="space-y-1 flex-1">
            <Label htmlFor="year-end-year">Geschäftsjahr</Label>
            <Input
              id="year-end-year"
              type="number"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              data-testid="year-end-year-input"
            />
          </div>
          <Button
            type="button"
            size="sm"
            className="rounded-full bg-slate-900 text-slate-50 px-4"
            disabled={loading}
            onClick={loadData}
            data-testid="year-end-refresh-button"
          >
            {loading ? "Lädt …" : "Aktualisieren"}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="rounded-full border-violet-300 text-violet-700 hover:bg-violet-50 px-4"
            onClick={async () => {
              try {
                const res = await apiClient.get(
                  `/companies/${companyId}/year-end-export?year=${parseInt(year, 10)}`,
                  { responseType: "blob" }
                );
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
                const link = document.createElement("a");
                link.href = url;
                link.download = `steuerberater_${year}.zip`;
                link.click();
                window.URL.revokeObjectURL(url);
                toast.success(`Steuerberater-Paket ${year} wurde heruntergeladen.`);
              } catch (e) {
                console.error(e);
                toast.error(e.response?.data?.detail || "Download fehlgeschlagen");
              }
            }}
            data-testid="year-end-export-zip-button"
          >
            📦 Treuhänder-ZIP
          </Button>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Treuhänder-ZIP enthält Journal, Bilanz, Erfolgsrechnung, MWST-Übersicht & alle Belegdateien des Jahres.
        </p>
      </Card>

      {/* Step 1.5: Checklist */}
      <YearEndChecklist companyId={companyId} year={year} />

      {/* Step 2: Adjustments */}
      <Card className="p-5 border border-slate-200 bg-white" data-testid="year-end-adjustments-card">
        <div className="mb-4">
          <h2 className="text-sm font-semibold text-slate-900">Abgrenzungen & Rückstellungen</h2>
          <p className="text-xs text-slate-500">
            Diese Buchungen werden automatisch per 31.12.{year} erfasst und fliessen in die Vorschau ein.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          {ADJUSTMENT_TYPES.map((t) => {
            const Icon = t.icon;
            const selected = form.adjustment_type === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setForm({ ...form, adjustment_type: t.id, balance_account_id: "" })}
                className={`text-left rounded-lg border p-3 transition-colors ${
                  selected ? `${t.tone} ring-2 ring-slate-900/10` : "border-slate-200 hover:bg-slate-50"
                }`}
                data-testid={`year-end-type-${t.id}`}
              >
                <div className="flex items-start gap-2.5">
                  <div className={`h-8 w-8 rounded-lg ${t.iconBg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900">{t.label}</div>
                    <div className="text-[11px] text-slate-600 mt-0.5">{t.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Iter125: Smart Suggestions Banner (Iter127: Wrapper always present) */}
        <div data-testid="year-end-smart-suggestions">
        {smartSuggestions.length > 0 && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1.5" data-testid="year-end-smart-suggestions-active">
            <div className="text-xs font-semibold text-amber-900 flex items-center gap-1.5">
              💡 Schlauer Vorschlag basierend auf deinen Buchungen:
            </div>
            {smartSuggestions.map((s) => (
              <div key={s.template_id} className="flex items-start justify-between text-xs gap-2">
                <div>
                  <span className="font-medium text-amber-900">{s.label}.</span>{" "}
                  <span className="text-amber-800">{s.reason}</span>
                  {s.suggested_amount != null && (
                    <span className="ml-1 font-mono text-amber-900">→ CHF {Number(s.suggested_amount).toLocaleString("de-CH", { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
                <div className="flex-shrink-0 flex items-center gap-1">
                  <button
                    type="button"
                    className="text-xs px-2 py-0.5 rounded bg-amber-100 hover:bg-amber-200 font-medium text-amber-900 border border-amber-300"
                    data-testid={`year-end-apply-suggestion-${s.template_id}`}
                    onClick={() => {
                      const tpl = templates.find((t) => t.id === s.template_id);
                      if (tpl) {
                        setForm({
                          adjustment_type: tpl.adjustment_type,
                          amount: s.suggested_amount != null ? String(s.suggested_amount) : "",
                          expense_or_income_account_id: tpl.expense_or_income_account_hint,
                          balance_account_id: tpl.balance_account_hint,
                          description: tpl.description_template,
                        });
                        toast.success(`Vorlage "${tpl.label}" ins Formular geladen — Betrag anpassen und buchen.`);
                      }
                    }}
                  >
                    Ins Formular
                  </button>
                  {s.suggested_amount != null && (
                    <button
                      type="button"
                      className="text-xs px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-700 font-semibold text-white border border-emerald-700"
                      data-testid={`year-end-book-suggestion-${s.template_id}`}
                      onClick={async () => {
                        const tpl = templates.find((t) => t.id === s.template_id);
                        if (!tpl) return;
                        if (!window.confirm(
                          `Direkt buchen: "${tpl.label}" — CHF ${Number(s.suggested_amount).toLocaleString("de-CH", { minimumFractionDigits: 2 })}?`
                        )) return;
                        try {
                          await apiClient.post(`/companies/${companyId}/year-end-adjustments`, {
                            year: parseInt(year, 10),
                            adjustment_type: tpl.adjustment_type,
                            amount: parseFloat(s.suggested_amount),
                            expense_or_income_account_id: tpl.expense_or_income_account_hint,
                            balance_account_id: tpl.balance_account_hint,
                            description: tpl.description_template,
                          });
                          toast.success(`✅ ${tpl.label} gebucht.`);
                          await loadData();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Direkt-Buchung fehlgeschlagen");
                        }
                      }}
                    >
                      🚀 Direkt buchen
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        </div>

        {/* Iter125: Vorlagen-Chips */}
        {templates.length > 0 && (
          <div className="mb-4" data-testid="year-end-templates">
            <div className="text-xs font-semibold text-slate-700 mb-2">Schnellauswahl (Schweizer KMU-Vorlagen):</div>
            <div className="flex flex-wrap gap-1.5">
              {templates.map((tpl) => (
                <button
                  key={tpl.id}
                  type="button"
                  className="px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-xs font-medium text-slate-700 border border-slate-200"
                  title={tpl.explanation}
                  data-testid={`year-end-template-${tpl.id}`}
                  onClick={() => {
                    setForm({
                      adjustment_type: tpl.adjustment_type,
                      amount: "",
                      expense_or_income_account_id: tpl.expense_or_income_account_hint,
                      balance_account_id: tpl.balance_account_hint,
                      description: tpl.description_template,
                    });
                    toast.success(`Vorlage "${tpl.label}" geladen.`);
                  }}
                >
                  <span className={`mr-1 inline-block w-1.5 h-1.5 rounded-full ${
                    tpl.category === "ARA" ? "bg-emerald-500" : tpl.category === "PRA" ? "bg-violet-500" : "bg-amber-500"
                  }`} />
                  {tpl.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] text-slate-500">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1" />ARA ·
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500 ml-2 mr-1" />PRA ·
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 ml-2 mr-1" />Rückstellung
              — beim Klick wird das Formular mit Defaults gefüllt.
            </p>
          </div>
        )}

        <form className="grid grid-cols-1 md:grid-cols-4 gap-3" onSubmit={submitAdjustment} data-testid="year-end-adjustment-form">
          <div className="space-y-1">
            <Label htmlFor="adj-amount">Betrag (CHF)</Label>
            <Input
              id="adj-amount"
              type="number"
              min="0"
              step="0.05"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              placeholder="0.00"
              data-testid="year-end-adj-amount"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="adj-account">{typeMeta.accountLabel}</Label>
            <Input
              id="adj-account"
              value={form.expense_or_income_account_id}
              onChange={(e) => setForm({ ...form, expense_or_income_account_id: e.target.value })}
              placeholder={typeMeta.accountPlaceholder}
              data-testid="year-end-adj-account"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="adj-balance">{typeMeta.balanceLabel}</Label>
            <Input
              id="adj-balance"
              value={form.balance_account_id}
              onChange={(e) => setForm({ ...form, balance_account_id: e.target.value })}
              placeholder={`Default: ${typeMeta.balanceDefault}`}
              data-testid="year-end-adj-balance"
            />
          </div>
          <div className="space-y-1 md:col-span-4">
            <Label htmlFor="adj-desc">Beschreibung</Label>
            <Input
              id="adj-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="z.B. Versicherung 01-12/26 bezahlt Dez 25"
              data-testid="year-end-adj-desc"
              required
            />
          </div>
          <div className="md:col-span-4 flex justify-end">
            <Button
              type="submit"
              size="sm"
              className="rounded-full bg-slate-900 text-slate-50 px-5"
              disabled={submittingAdj}
              data-testid="year-end-adj-submit"
            >
              {submittingAdj ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Buche …
                </>
              ) : (
                <>
                  <Plus className="h-3.5 w-3.5 mr-1.5" />
                  Anpassung buchen
                </>
              )}
            </Button>
          </div>
        </form>

        {adjustments.length > 0 && (
          <div className="mt-5 pt-4 border-t border-slate-200" data-testid="year-end-adjustments-list">
            <h3 className="text-xs font-semibold text-slate-700 mb-2">
              Erfasst ({adjustments.length})
            </h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Typ</TableHead>
                  <TableHead>Beschreibung</TableHead>
                  <TableHead>Konto</TableHead>
                  <TableHead>Gegenkonto</TableHead>
                  <TableHead className="text-right">Betrag</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {adjustments.map((a) => {
                  const meta = ADJUSTMENT_TYPES.find((t) => t.id === a.adjustment_type);
                  return (
                    <TableRow key={a.journal_entry_id} data-testid={`year-end-adj-row-${a.journal_entry_id}`}>
                      <TableCell>
                        <Badge className={`${meta?.tone || "bg-slate-100"} text-[10px] h-5 border`}>
                          {meta?.label || a.adjustment_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{a.description}</TableCell>
                      <TableCell className="text-xs font-mono">{a.expense_or_income_account_id}</TableCell>
                      <TableCell className="text-xs font-mono">{a.balance_account_id}</TableCell>
                      <TableCell className="text-right font-mono">{formatCHF(a.amount)}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteAdjustment(a.journal_entry_id)}
                          data-testid={`year-end-adj-delete-${a.journal_entry_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Step 3: Preview & Execute */}
      {preview && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="p-5 border border-slate-200 bg-white" data-testid="year-end-summary-card">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Zusammenfassung {preview.year}</h2>
            <div className="space-y-1.5 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Ertrag gesamt</span>
                <span className="font-mono font-medium">{formatCHF(preview.income_total)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Aufwand gesamt</span>
                <span className="font-mono font-medium">{formatCHF(preview.expense_total)}</span>
              </div>
              <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
                <span className="font-medium">Jahresergebnis</span>
                <span className={`font-mono font-semibold ${preview.result >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                  CHF {formatCHF(preview.result)} {preview.result >= 0 ? "(Gewinn)" : "(Verlust)"}
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-dashed border-slate-200 space-y-2">
              {closingStatus?.closed ? (
                <div className="space-y-2">
                  <div
                    className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center justify-between gap-3"
                    data-testid="year-end-closed-banner"
                  >
                    <span>
                      <strong>Jahresabschluss {preview.year} gebucht:</strong>{" "}
                      Ergebnis CHF {formatCHF(closingStatus.result)} wurde auf Konto{" "}
                      {closingStatus.result_account_id} (Gewinnvortrag) übertragen.
                    </span>
                    {!closingStatus.locked && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-full shrink-0"
                        onClick={undoYearEnd}
                        data-testid="year-end-undo-button"
                      >
                        Rückgängig
                      </Button>
                    )}
                  </div>
                  {closingStatus.locked ? (
                    <div
                      className="rounded-md border border-slate-300 bg-slate-900 px-3 py-2 text-xs text-slate-100 flex items-center justify-between gap-3"
                      data-testid="year-end-locked-banner"
                    >
                      <span>
                        🔒 <strong>Geschäftsjahr {preview.year} ist definitiv gesperrt</strong> — Buchungen
                        und Änderungen bis {closingStatus.locked_until} sind nicht mehr möglich.
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="rounded-full shrink-0 bg-transparent text-slate-100 border-slate-500 hover:bg-slate-800 hover:text-white"
                        onClick={() => lockYear(false)}
                        data-testid="year-end-unlock-button"
                      >
                        Entsperren
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {closingStatus.approval?.status === "approved" ? (
                        <p className="text-xs text-emerald-700" data-testid="year-end-approval-approved">
                          ✓ Vom Treuhänder freigegeben ({closingStatus.approval.approved_by})
                        </p>
                      ) : closingStatus.approval?.status === "requested" ? (
                        <div className="flex items-center gap-3">
                          <p className="text-xs text-amber-700" data-testid="year-end-approval-pending">
                            ⏳ Treuhänder-Freigabe angefragt ({closingStatus.approval.requested_by}) — ausstehend
                          </p>
                          <Button type="button" size="sm" variant="outline" className="rounded-full"
                            onClick={approveYear} data-testid="year-end-approve-button">
                            Als Treuhänder freigeben
                          </Button>
                        </div>
                      ) : (
                        <Button type="button" size="sm" variant="outline" className="rounded-full"
                          onClick={requestApproval} data-testid="year-end-request-approval-button">
                          Treuhänder-Freigabe anfragen
                        </Button>
                      )}
                      <div className="flex items-center gap-3">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="rounded-full border-slate-300"
                          onClick={() => lockYear(true)}
                          data-testid="year-end-lock-button"
                        >
                          🔒 Jahr {preview.year} definitiv sperren
                        </Button>
                        <p className="text-xs text-slate-500">
                          Sperrt alle Buchungen bis 31.12.{preview.year} gegen Änderungen. Empfohlen erst
                          nach Treuhänder-Freigabe.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Label htmlFor="year-end-result-account">Ergebniskonto (Gewinn/Verlust)</Label>
                  <div className="flex items-end gap-3">
                    <Input
                      id="year-end-result-account"
                      placeholder="z.B. 2970"
                      value={resultAccount}
                      onChange={(e) => setResultAccount(e.target.value)}
                      className="max-w-xs"
                      data-testid="year-end-result-account-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="rounded-full bg-emerald-600 text-emerald-50 px-4"
                      disabled={!resultAccount || loading}
                      onClick={executeYearEnd}
                      data-testid="year-end-execute-button"
                    >
                      Gewinn auf Gewinnvortrag übertragen
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Standard: 2970 Gewinnvortrag (wird bei Bedarf automatisch angelegt). Erstellt die
                    Abschlussbuchung per 31.12.{preview.year} — alle Erfolgspositionen werden auf das
                    Ergebniskonto abgeschlossen. Bilanz &amp; Erfolgsrechnung bleiben korrekt.
                  </p>
                </>
              )}
            </div>
          </Card>

          <Card className="p-5 border border-slate-200 bg-white" data-testid="year-end-accounts-card">
            <h2 className="text-sm font-semibold text-slate-900 mb-3">Ertrags- und Aufwandskonten</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-xs font-semibold text-slate-500 mb-1">Ertragskonten</h3>
                <Table data-testid="year-end-income-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Konto</TableHead>
                      <TableHead className="text-right text-xs">Betrag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.income_accounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-xs text-slate-400 text-center">
                          Keine Ertragskonten mit Saldo.
                        </TableCell>
                      </TableRow>
                    ) : (
                      preview.income_accounts.map((line) => (
                        <TableRow key={line.account_id}>
                          <TableCell className="text-xs font-mono">{line.account_id}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{formatCHF(line.balance)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
              <div>
                <h3 className="text-xs font-semibold text-slate-500 mb-1">Aufwandskonten</h3>
                <Table data-testid="year-end-expense-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Konto</TableHead>
                      <TableHead className="text-right text-xs">Betrag</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.expense_accounts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} className="text-xs text-slate-400 text-center">
                          Keine Aufwandskonten mit Saldo.
                        </TableCell>
                      </TableRow>
                    ) : (
                      preview.expense_accounts.map((line) => (
                        <TableRow key={line.account_id}>
                          <TableCell className="text-xs font-mono">{line.account_id}</TableCell>
                          <TableCell className="text-right font-mono text-xs">{formatCHF(line.balance)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
